// Implements: F-UIX-019
/**
 * The ceilings editor's form, as pure functions: what the admin types, what is sent.
 *
 * The bounds are the server's (S4d-1, `usage/domain/policy.py`, probed on the lane backend):
 * a count of generations is a whole number in `[0, 100 000]`, an amount in `[0, 10^12]` micros,
 * i.e. up to one million dollars with at most six decimals. They are checked here so a value the
 * server would refuse is never sent; the server still decides, and its refusal is shown too.
 *
 * Money is typed in dollars and turned into micros from its digits, never through a float, so
 * `0.000001` is exactly `1` and `1000000` exactly `10^12`. A point or a comma is the decimal mark,
 * but "1,000" or "1.000" (one to three digits, not starting with 0, then exactly three) is refused
 * as ambiguous: es-PE writes a thousand dollars "1,000.00" and es-ES "1.000,00", so guessing would
 * turn a thousand-dollar ceiling into a one-dollar one (fix round 1). "0,500" stays valid: a
 * thousands group never follows a leading zero. An empty field is `null`: that field
 * has no bound (the organization total then is the sum of its people's budgets).
 */

import type { CeilingRequest } from "../../../types/ceiling";
import { microsDecimal } from "./money";

/** The most generations a person's monthly ceiling may name (`MAX_MONTHLY_GENERATIONS`). */
export const MAX_GENERATIONS = 100_000;

/** The most micros a monthly budget may name (`MAX_MONTHLY_BUDGET_MICROS`): one million dollars. */
export const MAX_BUDGET_MICROS = 10 ** 12;

const PLACES = 6;
const WHOLE_RE = /^\d+$/;
/** Dollars: digits, then optionally a point or a comma and at most six decimals. */
// eslint-disable-next-line security/detect-unsafe-regex -- linear: the optional decimal group starts with `[.,]`, which `\d` cannot match, so no input has two parses.
const AMOUNT_RE = /^(\d+)(?:[.,](\d{0,6}))?$/;
/** A thousands group or three decimals? Refused rather than guessed. */
const AMBIGUOUS_RE = /^[1-9]\d{0,2}[.,]\d{3}$/;

/** What the admin typed, one text per field. */
export interface CeilingDraft {
  readonly generations: string;
  readonly budget: string;
  readonly total: string;
}

export type CeilingField = keyof CeilingDraft;

/** Why a field is refused: out of bounds (or not a number), or written with a thousands separator. */
export type FieldError = "range" | "thousands";

export type FieldErrors = Partial<Record<CeilingField, FieldError>>;

export type CeilingParse =
  | { readonly ok: true; readonly body: CeilingRequest }
  | { readonly ok: false; readonly errors: FieldErrors };

/** `5250000` micros -> `"5.25"`: the stored amount as the admin would type it. */
function dollarsText(micros: number): string {
  return microsDecimal(micros).replace(/\.?0+$/, "");
}

/** The form filled with the stored ceiling; an unbounded field is empty. */
export function draftOf(view: CeilingRequest): CeilingDraft {
  return {
    generations: view.max_generations_per_person === null ? "" : String(view.max_generations_per_person),
    budget: view.max_budget_micros_per_person === null ? "" : dollarsText(view.max_budget_micros_per_person),
    total: view.organization_budget_micros === null ? "" : dollarsText(view.organization_budget_micros),
  };
}

/** A whole count in bounds, `null` for empty, `undefined` when out of bounds or not a count. */
function generationsOf(text: string): number | null | undefined {
  const value = text.trim();
  if (value === "") return null;
  if (!WHOLE_RE.test(value)) return undefined;
  const count = Number(value);
  return count <= MAX_GENERATIONS ? count : undefined;
}

/** Exact micros of a dollar amount, `null` for empty, `"thousands"` or `undefined` when refused. */
function microsOf(text: string): number | null | "thousands" | undefined {
  const value = text.trim();
  if (value === "") return null;
  if (AMBIGUOUS_RE.test(value)) return "thousands";
  const match = AMOUNT_RE.exec(value);
  if (match === null) return undefined;
  const whole = match[1] ?? "0";
  const fraction = (match[2] ?? "").padEnd(PLACES, "0");
  const micros = Number(whole) * 10 ** PLACES + Number(fraction);
  return micros <= MAX_BUDGET_MICROS ? micros : undefined;
}

/**
 * The exact micros the admin's text will be saved as: `null` for empty (no bound), `undefined`
 * when the text is refused. The form shows it beside each money field before saving.
 */
export function amountMicros(text: string): number | null | undefined {
  const micros = microsOf(text);
  return micros === "thousands" ? undefined : micros;
}

function isAmount(micros: number | null | "thousands" | undefined): micros is number | null {
  return micros === null || typeof micros === "number";
}

function amountError(micros: number | null | "thousands" | undefined): FieldError | undefined {
  return micros === "thousands" ? "thousands" : micros === undefined ? "range" : undefined;
}

/** The body to send, or the fields that are refused and why. */
export function parseCeilingDraft(draft: CeilingDraft): CeilingParse {
  const generations = generationsOf(draft.generations);
  const budget = microsOf(draft.budget);
  const total = microsOf(draft.total);
  if (generations === undefined || !isAmount(budget) || !isAmount(total)) {
    const errors: FieldErrors = {};
    if (generations === undefined) errors.generations = "range";
    const budgetError = amountError(budget);
    if (budgetError !== undefined) errors.budget = budgetError;
    const totalError = amountError(total);
    if (totalError !== undefined) errors.total = totalError;
    return { ok: false, errors };
  }
  return {
    ok: true,
    body: {
      max_generations_per_person: generations,
      max_budget_micros_per_person: budget,
      organization_budget_micros: total,
    },
  };
}

/** A field set now, and lower than before or newly bounded: it may cut what was distributed. */
function tighter(before: number | null, after: number | null): boolean {
  return after !== null && (before === null || after < before);
}

/**
 * Whether the new ceiling is stricter than the stored one in any field. The platform admin
 * cannot read the organization's distribution (its policy answers them `404`), so this is what
 * the editor can tell: a stricter ceiling may now sit below the limits already given, which the
 * server accepts and clamps on read (N3), never refuses.
 */
export function tightens(before: CeilingRequest, after: CeilingRequest): boolean {
  return (
    tighter(before.max_generations_per_person, after.max_generations_per_person) ||
    tighter(before.max_budget_micros_per_person, after.max_budget_micros_per_person) ||
    tighter(before.organization_budget_micros, after.organization_budget_micros)
  );
}
